import { DragEvent, useState } from "react"
import { Box, MenuItem } from "@mui/material"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { enqueueSnackbar } from "notistack"
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"

import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { getEditProductGroupRoute } from "../../../../../constants/routes"
import {
    deleteProductGroup,
    fetchProductGroups,
    FetchProductGroupsResponse,
    ProductGroupQuery,
} from "../productGroupApi"
import { sortProductGroupInPriceList } from "../../priceList/priceListApi"
import { ProductGroupItem } from "../productGroupTypes"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { makeStyles } from "tss-react/mui"
import { ProductGroupInfoIconsStandalone } from "../../products/components/tableElements/ProductInfoIcons"
import { useMatchEnvironment } from "../../../../environment/envHooks"
import { EnvironmentType } from "../../../../environment/envTypes"

const useStyles = makeStyles()((theme) => ({
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
    iconRow: {
        marginTop: ".25rem",
    },
}))

function transformData(data: FetchProductGroupsResponse) {
    return data.productGroups
}

type AllProductGroupsTabProps = Readonly<{
    priceListId: number
    toolbarLeftContent?: React.ReactNode
}>

export function AllProductGroupsTab({ priceListId, toolbarLeftContent }: AllProductGroupsTabProps) {
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const navigate = useNavigate()

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [confirmModalText, setConfirmModalText] = useState("")
    const [productGroupId, setProductGroupId] = useState<number>()

    const productGroups = useApiQuery(fetchProductGroups, {
        queryName: ProductGroupQuery.LIST,
        dependencies: {
            merchantId,
            priceListId,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(productGroups)

    const columns = useMemo<MRT_ColumnDef<ProductGroupItem>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("product-group-code-column-header"),
                id: "productGroupCode",
                accessorFn: (row) => row.productGroupCode?.number ?? "",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("product-group-name-column-header"),
                accessorKey: "name",
                Cell: ({ row }) => (
                    <Box>
                        <Box>{row.original.name}</Box>
                        <Box className={classes.iconRow}>
                            <ProductGroupInfoIconsStandalone
                                availabilityDetails={row.original.availabilityDetails}
                                securitySettings={row.original.securitySettings}
                            />
                        </Box>
                    </Box>
                ),
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("product-group-status-column-header"),
                },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n, classes.iconRow],
    )

    const { mutateAsync: callDeleteProductGroup } = useApiMutation(deleteProductGroup, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const { mutateAsync: callSortProductGroup } = useApiMutation(sortProductGroupInPriceList, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const allowSort = !useMatchEnvironment([EnvironmentType.QA, EnvironmentType.Prod])

    return (
        <>
            <ApiQueryTable
                enableStickyHeader
                columns={columns}
                enableRowOrdering={allowSort}
                muiTableBodyRowDragHandleProps={({ table }) => ({
                    onDragEnd: (_event: DragEvent<HTMLButtonElement>) => {
                        const { draggingRow, hoveredRow } = table.getState()
                        if (!draggingRow || !hoveredRow || !productGroups.data) return

                        const targetRow = hoveredRow as MRT_Row<ProductGroupItem>
                        const targetIdx = productGroups.data.findIndex((g) => g.id === targetRow.original.id)

                        if (targetIdx === -1) return

                        callSortProductGroup({
                            priceListId,
                            productGroupId: draggingRow.original.id,
                            body: {
                                moveTo: { position: targetIdx + 1 },
                            },
                        })
                    },
                })}
                renderTopToolbarCustomActions={toolbarLeftContent ? () => <>{toolbarLeftContent}</> : undefined}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="edit" key="edit">
                        <MenuItem
                            onClick={() => {
                                navigate(getEditProductGroupRoute(row.original.id))
                                closeMenu()
                            }}
                        >
                            Edit
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="delete" key="delete">
                        <MenuItem
                            className={classes.deleteItem}
                            onClick={() => {
                                setShowConfirmModal(true)
                                setConfirmModalText(row.original.name)
                                setProductGroupId(row.original.id)
                                closeMenu()
                            }}
                        >
                            Delete
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
            />

            {showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("product-group-delete")}
                    entityName={confirmModalText}
                    handleClick={() => {
                        if (productGroupId) {
                            callDeleteProductGroup({ productGroupId, merchantId: merchantId ?? 0 })
                        } else {
                            throw new Error("Product group id is undefined")
                        }
                    }}
                    onClose={() => setShowConfirmModal(false)}
                    open={showConfirmModal}
                />
            )}
        </>
    )
}
